import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type {
  MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_PaginationState as PaginationState,
  MRT_Updater,
} from "material-react-table";
import NotificationQuery, {
  NotificationFilterParameters,
  NotificationOrderField,
  NotificationOrderParameters,
} from "notifications/_api/queries/notification-query";
import { BoxType } from "notifications/notification-box-type";

export type SortingState = Array<{ id: string; desc: boolean }>;
export type CommunicationTableSearchState = {
  globalFilter: string;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
  pagination: PaginationState;
};

export function getGlobalFilterFromSearchParams(searchParams: URLSearchParams): string {
  return searchParams.get("query") ?? "";
}

export function getColumnFiltersFromSearchParams(searchParams: URLSearchParams): ColumnFiltersState {
  const filters: ColumnFiltersState = [];
  const type = getOptionalSearchParam(searchParams, "type");
  const from = getOptionalSearchParam(searchParams, "from");
  const to = getOptionalSearchParam(searchParams, "to");
  const description = getOptionalSearchParam(searchParams, "description");

  if (type) filters.push({ id: "type", value: type });
  if (from) filters.push({ id: "from", value: from });
  if (to) filters.push({ id: "to", value: to });
  if (description) filters.push({ id: "description", value: description });

  return filters;
}

export function getSortingFromSearchParams(searchParams: URLSearchParams): SortingState {
  const orderBy = getNotificationOrderFieldFromColumnId(searchParams.get("orderBy"));
  if (!orderBy) return [];

  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir = Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam);

  return [
    {
      id: getNotificationColumnIdFromOrderField(orderBy),
      desc: orderDir !== OrderDirection.Ascending,
    },
  ];
}

export function getPaginationFromSearchParams(searchParams: URLSearchParams): PaginationState {
  const pagination = PaginationParameters.fromSearchParams(searchParams);

  return {
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  };
}

export function getNotificationQueryFromSearchParams(
  searchParams: URLSearchParams,
  activeTab: BoxType,
): NotificationQuery {
  return new NotificationQuery({
    filterParameters: new NotificationFilterParameters({
      query: getOptionalSearchParam(searchParams, "query"),
      boxType: activeTab,
      isActionRequired: getBooleanSearchParam(searchParams, "actionRequired"),
      isUnread: getBooleanSearchParam(searchParams, "unread"),
      type: getOptionalSearchParam(searchParams, "type"),
      from: getOptionalSearchParam(searchParams, "from"),
      to: getOptionalSearchParam(searchParams, "to"),
      description: getOptionalSearchParam(searchParams, "description"),
    }),
    orderParameters: getNotificationOrderParametersFromSearchParams(searchParams),
    paginationParameters: PaginationParameters.fromSearchParams(searchParams),
  });
}

export function getNotificationOrderParametersFromSearchParams(
  searchParams: URLSearchParams,
): NotificationOrderParameters {
  const orderBy =
    getNotificationOrderFieldFromColumnId(searchParams.get("orderBy")) ?? NotificationOrderField.CreatedDate;
  const orderDirParam = searchParams.get("orderDir")?.toLowerCase();
  const orderDir =
    Object.values(OrderDirection).find((direction) => direction.toLowerCase() === orderDirParam) ??
    OrderDirection.Descending;

  return new NotificationOrderParameters({ orderBy, orderDir });
}

export function getSearchParamsFromTableState(
  state: CommunicationTableSearchState,
  currentSearchParams: URLSearchParams,
): URLSearchParams {
  const { globalFilter, columnFilters, sorting, pagination } = state;
  const newSearchParams = new URLSearchParams();

  if (globalFilter) {
    newSearchParams.set("query", globalFilter);
  }

  const typeFilter = columnFilters.find((filter) => filter.id === "type")?.value as string | undefined;
  if (typeFilter) {
    newSearchParams.set("type", typeFilter);
  }

  const fromFilter = columnFilters.find((filter) => filter.id === "from")?.value as string | undefined;
  if (fromFilter) {
    newSearchParams.set("from", fromFilter);
  }

  const toFilter = columnFilters.find((filter) => filter.id === "to")?.value as string | undefined;
  if (toFilter) {
    newSearchParams.set("to", toFilter);
  }

  const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
  if (descriptionFilter) {
    newSearchParams.set("description", descriptionFilter);
  }

  const orderingField = sorting.find((sort) => getNotificationOrderFieldFromColumnId(sort.id));
  if (orderingField) {
    const orderBy = getNotificationOrderFieldFromColumnId(orderingField.id);
    if (orderBy) newSearchParams.set("orderBy", orderBy);
    newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
  }

  newSearchParams.set("pageIndex", (pagination.pageIndex ?? PaginationParameters.DEFAULT_PAGE_INDEX).toString());
  newSearchParams.set("pageSize", (pagination.pageSize ?? PaginationParameters.DEFAULT_PAGE_SIZE).toString());

  copySearchParam(currentSearchParams, newSearchParams, "unread");
  copySearchParam(currentSearchParams, newSearchParams, "actionRequired");

  return newSearchParams;
}

export function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

export function shouldPopulateNotificationQuery(params: {
  notificationQuery?: NotificationQuery;
  queryIsLoading: boolean;
  errorLoadingQuery?: unknown;
  queryResponse?: unknown;
}): boolean {
  const { notificationQuery, queryIsLoading, errorLoadingQuery, queryResponse } = params;

  return Boolean(notificationQuery && !queryIsLoading && !errorLoadingQuery && queryResponse === undefined);
}

function getNotificationOrderFieldFromColumnId(value: string | null | undefined): NotificationOrderField | undefined {
  const normalized = value?.toLowerCase();
  if (!normalized) return undefined;

  return Object.values(NotificationOrderField).find((field) => field.toLowerCase() === normalized);
}

function getNotificationColumnIdFromOrderField(orderField: NotificationOrderField): string {
  if (orderField === NotificationOrderField.CreatedDate) return "createdDate";
  return orderField.toLowerCase();
}

function getBooleanSearchParam(searchParams: URLSearchParams, paramName: string): boolean | undefined {
  if (!searchParams.has(paramName)) return undefined;
  return searchParams.get(paramName) === "true";
}

function getOptionalSearchParam(searchParams: URLSearchParams, paramName: string): string | undefined {
  return searchParams.get(paramName) || undefined;
}

function copySearchParam(source: URLSearchParams, target: URLSearchParams, paramName: string): void {
  const value = source.get(paramName);
  if (value !== null) target.set(paramName, value);
}
